import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { Button, Animation, Spinner } from 'components'
import { FaArrowDown, FaFileAlt } from 'react-icons/fa'
import { AnimateSharedLayout, motion } from 'framer-motion'
import SubmarkLogo from 'resources/Logo/submark-logo-2.png'
import { AnimatePresence } from 'framer-motion'
import { Header, Selector, AccountSelector, TagSelector, MultiSelector, TextInput } from '../../components'

import { 
    triggerContext, 
    clientContext, 
    titleContext, 
    notesContext,
    priorityOptions,
    pageContext,
    sectionContext,
    outflowTriggers,
    workflowContext
} from 'context'
import classes from './Outflow.module.css';

const Column = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 4;
    padding-bottom: 2rem;
`

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 0.5rem;
`

const SubmitSection = styled.div`
    justify-self: end;
`

const submitStyle = {
    backgroundColor: 'var(--focus-color)',
    width: 'min-content',
    fontWeight: 'bold',
    margin: '0.5rem 0'
}

const InflowScreen = props => {
    const {
        state,
        onSelect, // i.e. setState
        onNext,
        onSubmit, 
    } = props;
    const uid = useSelector((state) => state.auth.uid)
    const user = useSelector((state) => state.auth.user)
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const [triggerOptions, setOptions] = React.useState({})
    const [spinner, setSpinner] = React.useState(false)

    const workflow = state['workflow']
    const trigger = state['trigger']
    const action = state['action']

    React.useEffect(() => {
        Object.keys(triggerOptions).length > 0 && setSpinner(false)
    }, [triggerOptions])

    React.useEffect(() => {
        const configurations = trigger['client'] && clientContext[trigger['client']] && clientContext[trigger['client']] && ['configurations'];
        configurations && Object.values(configurations).map(config => {
            if(typeof config['pre_requisite'] === 'function' && config['pre_requisite'](state)){
                getTriggerOptions(state, 'trigger')
                setSpinner(true)
            }
        })
    }, [])

    ///////////////
    // UTILITIES //
    ///////////////
    const getPages = () => {
        const pages = data['pages']
        const timezone = user['timezone']

        let pageOptions = {
            'ANY': { title: 'Any page', value: 'ANY' }
        };
        for (const [pageID, page] of Object.entries(pages)) {
            if(page['status'] === pageContext.STATUS.ACTIVE){
                pageOptions[pageID] = {
                    title: page['page_name'],
                    // subtitle: `Created ${moment(page['create_date']).tz(timezone).format('MMM D Y')}`,
                    icon: page['icon'] || { type: 'element', value: <FaFileAlt/> },
                    value: pageID
                }
            }
        }
        return pageOptions
    }

    const getSections = () => {
        const timezone = user['timezone']
        let sectionOptions = {
            'ANY': { title: 'Any section', value: 'ANY' }
        }
        if(trigger.ext['pageID'] === 'ANY') { return sectionOptions }

        const page = data['pages'][trigger.ext['pageID']]
        const sectionIDs = page && page['sections']
        
        page && sectionIDs.map(sectionID => {
            const section = data['sections'][sectionID]
            if(section && section['status'] === sectionContext.STATUS.ACTIVE) {
                sectionOptions[sectionID] = {
                    title: section['section_name'],
                    // subtitle: `Created ${moment(section['create_date']).tz(timezone).format('MMM D Y')}`,
                    value: sectionID
                }
            }
        })
        return sectionOptions
    }

    const getTitle = (value) => {
        const oldTitle = workflow['workflowTitle'] 
        const triggerSelected = value || action['action']
        const appSelected = action['client']
        if(!appSelected){
            return 'Untitled'
        } else if (appSelected && !triggerSelected) {
            return 'Untitled'
        } else if (appSelected && triggerSelected) {
            if(oldTitle === 'Untitled'){
                return `${clientContext[appSelected]['title']} - ${triggerContext[appSelected][triggerSelected]['title']}`
            } else {
                return oldTitle
            }
        }
    }

    const onSelectAccountHandler = (value) => {
        // Update Editor state
        let updatedState = {...state, 
            clientId: value, 
            action: {...action, clientId: value['clientId'], clientTitle: value['clientTitle']}, 
            error: { action: { ext: {} }, action: { exit: {} } }
        }
        onSelect(updatedState)

        // Get options for additional selectors (if any)
        const configurations = clientContext[action['client']]['configurations'];
        configurations && Object.values(configurations).map(config => {
            if(typeof config['pre_requisite'] === 'function' && config['pre_requisite'](updatedState)){
                getTriggerOptions(updatedState, 'clientId')
                setSpinner(true)
            }
        })
    }

    const onSelectTrigger = (value) => {
        // Update Editor state
        let updatedState = {...state, error: { trigger: { ext: {} }, action: { ext: {} } }, 
                            action: {...action, action: value}, 
                            workflow: {...workflow, workflowTitle: getTitle(value)} }
        onSelect(updatedState)

        // Get options for additional selectors (if any)
        const configurations = clientContext[trigger['client']]['configurations'];
        configurations && Object.values(configurations).map(config => {
            if(typeof config['pre_requisite'] === 'function' && config['pre_requisite'](updatedState)){
                getTriggerOptions(updatedState, 'trigger')
                setSpinner(true)
            } else {
                setOptions({})
            }
        })
    }

    const getExtTriggers = () => {
        if(!trigger['client']){ return }
        
        const configurations = clientContext[trigger['client']]['configurations'];
        let components = [];
        if(!configurations){ return }

        for (const [key, trigger] of Object.entries(configurations)) {
            // Create components
            if(trigger['selector'] === 'Selector') {
                trigger.pre_requisite(state) && components.push(
                <Selector label={trigger.label}
                    value={state['trigger'].ext && state['trigger'].ext[key]} 
                    options={trigger.options} 
                    onSelect={(value) => onSelect({ ...state, trigger: {...state.trigger, ext: {...state.trigger.ext, [key]: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                    error={state.error.trigger.ext[key]}
                    refreshOptions={() => getTriggerOptions(state)}/>)
            } else if (trigger['selector'] === 'MultiSelector') {
                
            } else if (trigger['selector'] === 'Text') {
                components.push(
                    <TextInput label={triggerOptions[key]['label']} 
                        value={state['trigger'].ext && state['trigger'].ext[key]} 
                        error={state.error.trigger.ext[key]}
                        onChange={(value) => onSelect({ ...state, trigger: {...state.trigger, ext: {...state.trigger.ext, [key]: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })}/>
                )
            } else if (trigger['selector'] === 'TagSelector') {
                trigger.pre_requisite(state) && components.push(
                    <TagSelector pageID={state.trigger.ext['pageID']} selected={state.trigger.ext[key]}
                        error={state.error.trigger.ext[key]}
                        onSelect={(value) => onSelect({ ...state, trigger: {...state.trigger, ext: { ...state.trigger.ext, tags: value}},error: { trigger: { ext: {} }, action: { ext: {} } } })}/>
                )
            }
        }

        return components
    }

    const getTriggerOptions = async (updatedState) => {
        const configurations = clientContext[updatedState.trigger['client']]['configurations'];
        let options = {};

        if(!configurations) { return }

        for (const [key, trigger] of Object.entries(configurations)) {
            // Check for pre-requisite are fufilled
            if(typeof trigger['pre_requisite'] === 'function' && trigger['pre_requisite'](updatedState)){ 
                // Get options            
                if(trigger['options'] && typeof trigger['options'] == 'function'){
                    await trigger['options']({...updatedState, userId: uid})
                    .then(option => {
                        // Create component
                        if(trigger['selector'] === 'Selector') {
                            options[key] = {label: trigger['label'], options: option['options']}
                        } else if (trigger['selector'] === 'MultiSelector') {
                            
                        }
                        
                    })
                    .catch(err => {
                        console.log('Cannot get options', err)
                    }) 
                } else if (['Text'].includes(trigger['selector'])){
                    options[key] = { label: trigger['label'] }
                }
            } 
        }

        Object.keys(options).length > 0 && setOptions(options)
    }

    ////////////////
    // COMPONENTS //
    ////////////////
    // Main triggers
    const triggerSection =
    <div className={state.section === 'TRIGGER' ? classes.TriggerOpen : classes.TriggerClosed} 
        onClick={() => state.section !== 'TRIGGER' && onSelect({ section: 'TRIGGER', error: { trigger: { ext: {} }, action: { ext: {} } } })}>
        <Header title='Atomicly' subtitle='Trigger' iconBackgroundColor='var(--focus-color)'
            icon={<img src={SubmarkLogo} style={{width:'auto', height:'1.3rem',objectFit:'contain', marginRight:'0.1rem'}}/>}/>
        <AnimatePresence initial={false}>
            {state.section === 'TRIGGER' && 
            <Animation animation='expand'>
                <AnimateSharedLayout>
                    <Column>
                        <motion.div layout>
                            <Selector label='Select trigger' 
                                value={trigger['trigger']} 
                                options={outflowTriggers} 
                                onSelect={(value) => onSelect({ ...state, trigger: {client: 'ATOMICLY', trigger: value, clientId: uid, ext: {}}, workflow: {...workflow, workflowTitle: 'Untitled'},error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                                error={state.error.trigger['trigger']}/>
                        </motion.div>

                        <motion.div layout>
                            <Row>
                                <Selector label='Page' 
                                    value={trigger.ext['pageID']} 
                                    options={getPages()} 
                                    onSelect={(value) => onSelect({ ...state, trigger: {...trigger, ext: { ...trigger.ext, pageID: value, sectionID: value === 'ANY' && 'ANY'}}, error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                                    error={state.error.trigger.ext['pageID']}/>
                                
                                <Selector label='Section' 
                                    value={trigger.ext['sectionID']} 
                                    options={getSections()} 
                                    onSelect={(value) => onSelect({ ...state, trigger: {...trigger, ext: { ...trigger.ext, sectionID: value}},  error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                                    error={state.error.trigger.ext['sectionID']} optionPlaceholder='Please select a page first'/>
                            </Row>
                        </motion.div>

                        <motion.div layout>
                            {/* Additional selector */}
                            {getExtTriggers()}
                        </motion.div>

                        {/* {trigger['trigger'] === 'PriorityItem' &&
                        <Selector label='Priority' error={state.error.trigger.ext['priority']}
                            value={trigger.ext['priority']} 
                            options={priorityOptions} 
                            onSelect={(value) => onSelect({ ...state, trigger: {...trigger, ext: { ...trigger.ext, priority: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })}/>}
                        
                        {trigger['trigger'] === 'LabelItem' && 
                        <TagSelector pageID={trigger.ext['pageID']} selected={trigger.ext['tags']}
                            error={state.error.trigger.ext['tags']}
                            onSelect={(value) => onSelect({ ...state, trigger: {...trigger, ext: { ...trigger.ext, tags: value}},error: { trigger: { ext: {} }, action: { ext: {} } } })}/>} */}
                    </Column>
                </AnimateSharedLayout>
            </Animation>}
        </AnimatePresence>

        {state.section === 'TRIGGER' && <SubmitSection><Button text='Next' styles={submitStyle} onSelect={onNext}/></SubmitSection>}
    </div>

    const actionSection =
    <div className={state.section === 'ACTION' ? classes.ActionOpen : classes.ActionClosed} >
        <Header title='Select app' subtitle='Select app' 
            icon={action.client && <img src={clientContext[action['client']]['icon']['value']} style={{width:'auto', height:'1.5rem',objectFit:'contain'}}/>}/>

        <AnimatePresence>
            {state.section === 'ACTION' &&
            <Animation animation='expand'>
                <Column>
                    <motion.div layout>
                        <Selector label='Select app' 
                            value={action['client']} 
                            options={_.pick(clientContext, workflowContext.OUTFLOW)} 
                            onSelect={(value) => {
                                onSelect({ ...state, action: {client: value, action: null, clientId: null}, workflow: {...workflow, workflowTitle: 'Untitled'},error: { trigger: { ext: {} }, action: { ext: {} } } })
                                setOptions({})
                            }} 
                            error={state.error.action['client']}/>
                    </motion.div>

                    <motion.div layout>
                        {action['client'] &&
                        <Selector label='Select action' 
                            value={action['action']} 
                            options={triggerContext[action['client']]} 
                            onSelect={(value) => onSelect({ ...state, workflow: {...workflow, workflowTitle: getTitle()}, action: {...action, action: value}, error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                            error={state.error.action['action']}/>}
                    </motion.div>

                    <motion.div layout>
                        {action['client'] &&
                        <AccountSelector value={action['clientId']} selectedClient={action['client']}
                            onSelect={onSelectAccountHandler} 
                            onError={(value) => onSelect({...state, error: {...state.error, clientId: value}})}
                            error={state.error.action['clientId']} />}
                    </motion.div>

                </Column>
            </Animation>}
        </AnimatePresence>

        {state.section === 'ACTION' && <SubmitSection><Button text='Save' styles={submitStyle} onSelect={onSubmit}/></SubmitSection>}
    </div>

    const arrow = <FaArrowDown style={{fontSize: '2rem', margin: '1rem', color: 'var(--primary-accent-color)'}}/>


    return (
        <AnimateSharedLayout type="crossfade">
            <motion.div >{triggerSection}</motion.div>
            <motion.div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{arrow}</motion.div>
            <motion.div >{actionSection}</motion.div>
        </AnimateSharedLayout>
    )
}

export default InflowScreen